import type { AgeCard as AgeCardDto, BoardMetricKey } from 'chery-contracts';
import { formatValue } from 'chery-kpi-engine';
import { cn } from '../../lib/cn';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { formatDayMonth } from '../system/system-display';
import { compactNumber, fillPercent, keyList, metricText, percentText } from './board-display';
import { AGE_COLOR, AGE_COPY } from './content/ages';

export interface AgeCardProps {
  card: AgeCardDto;
  /** Keys the card reads that are unbound. */
  unbound: readonly BoardMetricKey[];
}

interface GateBarProps {
  label: string;
  /** `null` = unbound or no data — shown `—`, never 0. */
  actual: number | null;
  gate: number;
  actualText: string;
  gateText: string;
  pct: number | null;
  testId: string;
  hint: string | null;
  /** The age's own colour (board one-sheet), used for the filled part of the bar. */
  color: string;
}

/** Gate vs actual: a muted track for the gate, the actual filled in the age's colour, the % of gate at the end. */
const GateBar = ({ label, actual, gate, actualText, gateText, pct, testId, hint, color }: GateBarProps) => {
  const { t } = useI18nTranslation();

  return (
    <div
      className="space-y-1"
      data-gate={testId}
    >
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono tabular-nums text-foreground">
          <span
            className="font-semibold"
            data-actual
          >
            {actualText}
          </span>
          <span className="text-muted-foreground"> / {gateText}</span>
        </span>
      </div>
      <div
        className="h-2.5 rounded-sm bg-muted"
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={gate}
        aria-valuenow={actual ?? undefined}
      >
        <div
          className="h-full rounded-e-[4px] rounded-s-sm"
          style={{ width: `${fillPercent(actual, gate)}%`, background: color }}
        />
      </div>
      <p className="text-[11px] text-muted-foreground">
        {hint ? (
          <span
            className="italic"
            data-unbound-hint
          >
            {hint}
          </span>
        ) : (
          <span data-pct>{t('board.direction.ofGate', { pct: percentText(pct) })}</span>
        )}
      </p>
    </div>
  );
};

/** One Age (plan §8.2): its gates, and for a started age the live actuals, % of gate and the last-4-weeks run-rate. */
export const AgeCard = ({ card, unbound }: AgeCardProps) => {
  const { t } = useI18nTranslation();
  const copy = AGE_COPY[card.age];
  const progress = card.progress;
  const unitsUnbound = unbound.includes('VOLUME_DELIVERED');
  const revenueUnbound = unbound.includes('VEHICLE_REVENUE');
  const isCurrent = card.status === 'CURRENT';
  const ageColor = AGE_COLOR[card.age];

  return (
    <article
      className={cn('flex flex-col gap-4 rounded-lg border border-border border-t-4 bg-card p-5', isCurrent && 'ring-1 ring-primary/30')}
      style={{ borderTopColor: ageColor }}
      data-age-card={card.age}
      data-age-status={card.status}
    >
      <header className="flex items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold text-foreground">{card.label}</h2>
        <span
          className={cn('text-xs font-medium uppercase tracking-wide', !isCurrent && 'text-muted-foreground')}
          style={isCurrent ? { color: ageColor } : undefined}
        >
          {isCurrent ? copy.tag : t(`board.direction.status${card.status}`)}
        </span>
      </header>

      <div>
        <p className="font-mono text-4xl font-semibold tabular-nums text-foreground">{card.unitGate.toLocaleString('en-US')}</p>
        <p className="text-xs text-muted-foreground">{copy.unitLine}</p>
        <p className="mt-1 text-xs text-muted-foreground">
          {t('board.direction.revenueGate')}: <span className="font-mono text-foreground">{compactNumber(card.revenueGate)} JOD</span> ·{' '}
          {t('board.direction.exitLine')}: {card.exitLine}
        </p>
      </div>

      <p className="text-sm leading-relaxed text-muted-foreground">{copy.blurb}</p>

      {progress ? (
        <div
          className="mt-auto space-y-3 border-t border-border pt-3"
          data-age-progress
        >
          <GateBar
            testId="units"
            label={`${t('board.direction.units')} · ${t('board.direction.toDate')}`}
            actual={progress.units}
            gate={card.unitGate}
            actualText={metricText(progress.units, 'NUMBER', null)}
            gateText={card.unitGate.toLocaleString('en-US')}
            pct={progress.unitsPct}
            hint={unitsUnbound ? t('board.unboundHint', { keys: keyList(['VOLUME_DELIVERED']) }) : null}
            color={ageColor}
          />
          <GateBar
            testId="revenue"
            label={`${t('board.direction.revenue')} · ${t('board.direction.toDate')}`}
            actual={progress.revenue}
            gate={card.revenueGate}
            actualText={metricText(progress.revenue, 'CURRENCY', 'JOD')}
            gateText={`${compactNumber(card.revenueGate)} JOD`}
            pct={progress.revenuePct}
            hint={revenueUnbound ? t('board.unboundHint', { keys: keyList(['VEHICLE_REVENUE']) }) : null}
            color={ageColor}
          />
          <div
            className="flex items-baseline justify-between gap-2 rounded-md bg-muted/60 px-3 py-2 text-xs"
            data-run-rate
          >
            <span className="text-muted-foreground">
              {t('board.direction.runRate')}
              <span className="ms-1">
                (
                {t('board.direction.runRateRange', {
                  from: formatDayMonth(progress.runRate.fromWeek.weekDate),
                  to: formatDayMonth(progress.runRate.toWeek.weekDate),
                })}
                )
              </span>
            </span>
            <span
              className="font-mono font-semibold tabular-nums text-foreground"
              data-run-rate-value
            >
              {progress.runRate.units === null
                ? metricText(null, 'NUMBER', null)
                : t('board.direction.runRateValue', { units: formatValue('NUMBER', progress.runRate.units) })}
            </span>
          </div>
        </div>
      ) : (
        <p
          className="mt-auto border-t border-border pt-3 text-xs italic text-muted-foreground"
          data-gates-only
        >
          {card.status === 'FUTURE' ? t('board.direction.gatesOnly') : t('board.direction.noWeeks')}
        </p>
      )}
    </article>
  );
};
